/**
 * Figma Import Service
 * Handles fetching files from Figma API and converting nodes to Pop Onboarding elements.
 */

import { supabase } from '../supabaseClient';

export const fetchFigmaFile = async (fileId, token) => {
    try {
        const response = await fetch(`https://api.figma.com/v1/files/${fileId}`, {
            headers: {
                'X-Figma-Token': token
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.err || 'Failed to fetch Figma file');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching Figma file:', error);
        throw error;
    }
};

// NEW: Fetch rendered images for frames
export const fetchFigmaImages = async (fileId, nodeIds, token) => {
    try {
        const ids = nodeIds.join(',');
        const response = await fetch(
            `https://api.figma.com/v1/images/${fileId}?ids=${ids}&format=png&scale=2`,
            {
                headers: {
                    'X-Figma-Token': token
                }
            }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.err || 'Failed to fetch Figma images');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching Figma images:', error);
        throw error;
    }
};

// NEW: Detect interactive nodes (buttons, inputs) in a frame
const detectInteractiveNodes = (node, rootX, rootY, interactiveNodes = []) => {
    if (!node.children) return interactiveNodes;

    node.children.forEach(child => {
        if (child.visible === false || !child.absoluteBoundingBox) return;

        const x = child.absoluteBoundingBox.x - rootX;
        const y = child.absoluteBoundingBox.y - rootY;
        const width = child.absoluteBoundingBox.width;
        const height = child.absoluteBoundingBox.height;

        // Detect buttons: Look for text nodes with "button", "btn", "next", "continue" etc in name
        const isButton = child.name.toLowerCase().match(/button|btn|next|continue|submit|start|get started|sign up|login/);

        // Detect inputs: Look for rectangles with "input", "field", "text field" in name
        const isInput = child.name.toLowerCase().match(/input|field|text field|email|password|name/);

        if (isButton && child.type === 'TEXT') {
            interactiveNodes.push({
                id: `btn-${child.id.replace(/:/g, '-')}`,
                type: 'button',
                x,
                y,
                width,
                height,
                content: child.characters || 'Button',
                action: { type: 'navigate', target: 'next' },
                isOverlay: true,
                styles: {
                    color: convertColor(child.fills?.[0]?.color),
                    fontSize: child.style?.fontSize ? `${child.style.fontSize}px` : '16px',
                    fontWeight: child.style?.fontWeight || 600,
                    fontFamily: child.style?.fontFamily || 'Inter',
                }
            });
        } else if (isInput && (child.type === 'RECTANGLE' || child.type === 'FRAME')) {
            interactiveNodes.push({
                id: `input-${child.id.replace(/:/g, '-')}`,
                type: 'input',
                x,
                y,
                width,
                height,
                placeholder: child.name.replace(/input|field/gi, '').trim() || 'Enter text',
                isOverlay: true,
                styles: {}
            });
        }

        // Recurse into groups and frames
        if (child.type === 'GROUP' || child.type === 'FRAME' || child.type === 'INSTANCE' || child.type === 'COMPONENT') {
            detectInteractiveNodes(child, rootX, rootY, interactiveNodes);
        }
    });

    return interactiveNodes;
};



// NEW: Upload image to Supabase Storage
const uploadImageToSupabase = async (imageUrl, fileId, frameId) => {
    try {
        // 1. Fetch the image blob
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error('Failed to download image from Figma');
        const blob = await response.blob();

        // 2. Generate unique path
        const timestamp = Date.now();
        const filePath = `figma-imports/${fileId}/${frameId}_${timestamp}.png`;

        // 3. Upload to Supabase
        const { data, error } = await supabase.storage
            .from('project-assets')
            .upload(filePath, blob, {
                contentType: 'image/png',
                upsert: true
            });

        if (error) throw error;

        // 4. Get public URL
        const { data: { publicUrl } } = supabase.storage
            .from('project-assets')
            .getPublicUrl(filePath);

        return publicUrl;
    } catch (error) {
        console.error('Error uploading image to Supabase:', error);
        // Fallback to original URL if upload fails (it will expire but better than nothing)
        return imageUrl;
    }
};

// NEW: Parse Figma file with TRUE hybrid mode - background image + all editable elements
export const parseFigmaFileHybrid = async (fileData, fileId, token) => {
    const screens = [];
    const pages = fileData.document.children;

    // Collect all frame IDs for batch image fetch
    const frameIds = [];
    const frameMap = new Map();

    pages.forEach(page => {
        const frames = page.children.filter(node =>
            node.type === 'FRAME' && node.absoluteBoundingBox
        );

        frames.forEach(frame => {
            frameIds.push(frame.id);
            frameMap.set(frame.id, frame);
        });
    });

    // Fetch images for all frames
    const imagesData = await fetchFigmaImages(fileId, frameIds, token);

    // Process frames sequentially to handle uploads
    // We use a for...of loop or Promise.all to handle async uploads
    const framePromises = Array.from(frameMap.entries()).map(async ([frameId, frame]) => {
        const screenX = frame.absoluteBoundingBox.x;
        const screenY = frame.absoluteBoundingBox.y;

        // Extract ALL elements (text, buttons, shapes) - not just interactive ones
        const allElements = [];
        flattenNode(frame, allElements, screenX, screenY);

        // Get image URL
        let backgroundImage = imagesData.images[frameId] || null;

        // Upload to Supabase if we have an image
        if (backgroundImage) {
            backgroundImage = await uploadImageToSupabase(backgroundImage, fileId, frameId);
        }

        return {
            id: `screen-${frame.id}`,
            name: frame.name,
            type: 'hybrid', // Indicates image-based screen with editable overlays
            backgroundImage,
            width: frame.absoluteBoundingBox.width,
            height: frame.absoluteBoundingBox.height,
            backgroundColor: convertColor(frame.backgroundColor),
            elements: allElements, // All elements are editable overlays
            styles: {
                paddingTop: '0px',
                paddingBottom: '0px'
            }
        };
    });

    const processedScreens = await Promise.all(framePromises);
    screens.push(...processedScreens);

    return screens;
};

export const parseFigmaFile = (fileData) => {
    const screens = [];

    // Figma document structure: document -> pages -> frames (screens)
    const pages = fileData.document.children;

    pages.forEach(page => {
        // Find top-level frames which we'll treat as Screens
        // We filter for FRAME type and ensure it has a bounding box
        const frames = page.children.filter(node =>
            node.type === 'FRAME' && node.absoluteBoundingBox
        );

        frames.forEach(frame => {
            const screenX = frame.absoluteBoundingBox.x;
            const screenY = frame.absoluteBoundingBox.y;

            const elements = [];
            flattenNode(frame, elements, screenX, screenY);

            screens.push({
                id: `screen-${frame.id}`,
                name: frame.name,
                width: frame.absoluteBoundingBox.width,
                height: frame.absoluteBoundingBox.height,
                backgroundColor: convertColor(frame.backgroundColor),
                elements: elements,
                styles: {
                    paddingTop: '0px',
                    paddingBottom: '0px'
                }
            });
        });
    });

    return screens;
};

// Recursively flatten children into a single list of elements
const flattenNode = (node, elements, rootX, rootY) => {
    if (!node.children) return;

    node.children.forEach(child => {
        // Skip invisible nodes
        if (child.visible === false) return;

        const element = mapNodeToElement(child, rootX, rootY);

        if (element) {
            elements.push(element);
        }

        // Recurse for groups or frames to find nested content
        // We don't recurse if we already mapped it to an element (like a Button group if we supported that, but for now we flatten everything)
        // Actually, if we map a Rectangle, it has no children. 
        // If it's a GROUP or FRAME, we recurse.
        if (child.type === 'GROUP' || child.type === 'FRAME' || child.type === 'INSTANCE' || child.type === 'COMPONENT') {
            flattenNode(child, elements, rootX, rootY);
        }
    });
};

const mapNodeToElement = (node, rootX, rootY) => {
    if (!node.absoluteBoundingBox) return null;

    const x = node.absoluteBoundingBox.x - rootX;
    const y = node.absoluteBoundingBox.y - rootY;
    const width = node.absoluteBoundingBox.width;
    const height = node.absoluteBoundingBox.height;

    const baseElement = {
        id: `el-${node.id.replace(/:/g, '-')}`, // Sanitize ID
        x,
        y,
        width,
        height,
        styles: {}
    };

    // 1. TEXT
    if (node.type === 'TEXT') {
        return {
            ...baseElement,
            type: 'text',
            content: node.characters,
            styles: {
                ...baseElement.styles,
                color: convertColor(node.fills?.[0]?.color),
                fontSize: node.style?.fontSize ? `${node.style.fontSize}px` : '16px',
                fontWeight: node.style?.fontWeight || 400,
                fontFamily: node.style?.fontFamily || 'Inter',
                textAlign: node.style?.textAlignHorizontal?.toLowerCase() || 'left',
                // Handle opacity
                opacity: node.opacity
            }
        };
    }

    // 2. RECTANGLE (Mapped to empty Text with background)
    if (node.type === 'RECTANGLE' || node.type === 'ELLIPSE') {
        // Only map if it has a fill
        if (!node.fills || node.fills.length === 0 || node.fills[0].type !== 'SOLID') return null;

        return {
            ...baseElement,
            type: 'text',
            content: '', // Empty content for shape
            styles: {
                ...baseElement.styles,
                backgroundColor: convertColor(node.fills[0].color),
                borderRadius: node.cornerRadius ? `${node.cornerRadius}px` : (node.type === 'ELLIPSE' ? '50%' : '0px'),
                opacity: node.opacity
            }
        };
    }

    // 3. IMAGE (Figma uses image refs, need to fetch actual URLs separately usually, 
    // but for now we'll check if it's an image node. 
    // Note: Figma API doesn't give image URLs in the file response directly, 
    // you need to use the /images endpoint. 
    // For V1, we might skip images or use a placeholder if we can't easily get the URL in one go.
    // Let's map it to a placeholder for now or try to handle it if we have the ref.)
    if (node.type === 'RECTANGLE' && node.fills?.some(f => f.type === 'IMAGE')) {
        return {
            ...baseElement,
            type: 'image',
            src: 'https://via.placeholder.com/150?text=Figma+Image', // Placeholder for now as we need a 2nd API call
            styles: {
                ...baseElement.styles,
                borderRadius: node.cornerRadius ? `${node.cornerRadius}px` : '0px',
            }
        };
    }

    return null;
};

const convertColor = (color) => {
    if (!color) return 'transparent';
    const r = Math.round(color.r * 255);
    const g = Math.round(color.g * 255);
    const b = Math.round(color.b * 255);
    const a = color.a !== undefined ? color.a : 1;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

